/**
 * Voir-dire answer capture. Records the juror's ACTUAL answers to the model's
 * confirming probes. This is the compliance core: a recorded answer overrides
 * the model's estimate (see core/scoring-sv.ts voirDireAdjustment), and a
 * peremptory strike cites the recorded rating here, never a score/field/
 * inference (the Soares/Batson defense).
 *
 * COURTROOM CONSTRAINT: this screen is used live, in front of the judge and
 * the panel, on a handheld tablet — no keyboard, no typing, no dropdowns, no
 * sliders. Every control here is a single tap:
 *  - Favorability (favorable to my side / unfavorable / neutral) — labeled
 *    relative to whichever side is currently active; stored side-neutral.
 *  - Demeanor (positive/neutral/negative) — scales how much weight the
 *    paired favorability gets (a confident answer is more reliable evidence
 *    of the juror's actual leaning; an evasive one is less), not an
 *    independent direction of its own.
 *  - Cause flag — independent of the numeric score; marks a cause-challenge
 *    candidate (unlimited, decided by the judge, distinct from a peremptory).
 * Every tap persists and re-scores instantly — there is no separate "Save".
 * (A verbatim quote of the answer, if wanted for the record, is a follow-up
 * office task — typing it in here is not part of the in-court flow.)
 */

import type { Juror, VdAnswer, VdFavorability, VdDemeanor, Side, ChargeSlug } from '@/types';
import { scoreJurorSV } from '@/core/scoring-sv';
import { svAssessmentHtml } from '@/features/juror-sv';
import { store, panelContext, svProfile } from '@/app/store';

function blankAnswer(question: string, now: string): VdAnswer {
  return { question, answer: '', favorability: 'unrated', demeanor: 'unrated', cause: false, at: now };
}

function findOrCreate(list: VdAnswer[], question: string): VdAnswer {
  const existing = list.find((x) => x.question === question);
  if (existing) return existing;
  const created = blankAnswer(question, new Date().toISOString());
  list.push(created);
  return created;
}

// ---------- rendering ----------

function favorabilityButtons(current: VdFavorability, side: Side, jurorId: number, question: string): string {
  const favSide: VdFavorability = side === 'defense' ? 'favorable-defense' : 'favorable-prosecution';
  const unfavSide: VdFavorability = side === 'defense' ? 'favorable-prosecution' : 'favorable-defense';
  const opts: [VdFavorability, string, string][] = [
    [favSide, 'Favorable', '#16a34a'],
    ['neutral', 'Neutral', '#6b7280'],
    [unfavSide, 'Unfavorable', '#dc2626']
  ];
  const q = question.replace(/'/g, "\\'");
  return opts
    .map(([v, label, color]) => {
      const on = v === current;
      return (
        `<button onclick="setVdRating(${jurorId}, '${q}', {favorability:'${v}'})" style="border:1px solid ${on ? color : '#cbd5e1'};` +
        `background:${on ? color : '#fff'};color:${on ? '#fff' : '#475569'};border-radius:8px;padding:10px 14px;` +
        `font-size:13px;font-weight:700;cursor:pointer;min-height:40px;">${label}</button>`
      );
    })
    .join(' ');
}

function demeanorButtons(current: VdDemeanor, jurorId: number, question: string): string {
  const opts: [VdDemeanor, string, string][] = [
    ['positive', 'Confident', '#2563eb'],
    ['neutral', 'Neutral', '#6b7280'],
    ['negative', 'Evasive', '#b45309']
  ];
  const q = question.replace(/'/g, "\\'");
  return opts
    .map(([v, label, color]) => {
      const on = v === current;
      return (
        `<button onclick="setVdRating(${jurorId}, '${q}', {demeanor:'${v}'})" style="border:1px solid ${on ? color : '#cbd5e1'};` +
        `background:${on ? color : '#fff'};color:${on ? '#fff' : '#475569'};border-radius:8px;padding:10px 14px;` +
        `font-size:13px;font-weight:700;cursor:pointer;min-height:40px;">${label}</button>`
      );
    })
    .join(' ');
}

function causeToggle(current: boolean, jurorId: number, question: string): string {
  const q = question.replace(/'/g, "\\'");
  return (
    `<button onclick="setVdRating(${jurorId}, '${q}', {cause:${!current}})" style="border:1px solid ${current ? '#b91c1c' : '#cbd5e1'};` +
    `background:${current ? '#b91c1c' : '#fff'};color:${current ? '#fff' : '#475569'};border-radius:8px;padding:10px 14px;` +
    `font-size:13px;font-weight:700;cursor:pointer;min-height:40px;">${current ? '⚑ Cause' : 'Flag for cause'}</button>`
  );
}

export function vdCaptureHtml(j: Juror, ctx: { side: Side; activeCharges: ChargeSlug[] }, open = false): string {
  const a = scoreJurorSV(j, ctx, svProfile());
  const existing = j.vdAnswers || [];
  const answerFor = (q: string): VdAnswer | undefined => existing.find((x) => x.question === q);

  // Prompts = the model's confirming probes for this juror (deduped).
  const prompts = [...new Set(a.probes)];
  // Include any recorded answers whose question is no longer a live probe.
  for (const e of existing) if (!prompts.includes(e.question)) prompts.push(e.question);

  const causeCount = existing.filter((x) => x.cause).length;

  const esc = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  let rows = prompts
    .map((q) => {
      const ex = answerFor(q);
      return (
        `<div class="vd-row" style="margin-bottom:12px;border-bottom:1px dashed #eef2f7;padding-bottom:11px;">` +
        `<div style="font-size:12px;font-weight:600;color:#334155;margin-bottom:7px;">${esc(q)}</div>` +
        '<div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-bottom:6px;">' +
        // Deliberately the DURABLE side, not ctx.side (the panel lens). These
        // buttons write data, they do not read it: "Favorable" is stored
        // absolutely as favorable-defense / favorable-prosecution. Framing them
        // by the lens would be self-consistent but a trap — counsel who forgot
        // the lens was flipped would tap "Favorable" meaning good-for-my-client
        // and record the opposite. You always rate jurors as yourself.
        favorabilityButtons(ex?.favorability || 'unrated', store.getState().side, j.id, q) +
        '</div>' +
        '<div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;">' +
        demeanorButtons(ex?.demeanor || 'unrated', j.id, q) +
        causeToggle(!!ex?.cause, j.id, q) +
        '</div>' +
        '</div>'
      );
    })
    .join('');
  if (!rows) rows = '<div style="font-size:11px;color:var(--text-muted);">No open probes — tap through any voir-dire answers relevant to a strike.</div>';

  const causeBanner = causeCount
    ? `<div style="background:#fef2f2;border:1px solid #fecaca;color:#b91c1c;border-radius:8px;padding:7px 10px;font-size:11px;font-weight:700;margin-bottom:8px;">⚑ ${causeCount} answer(s) marked CAUSE — cause-challenge candidate.</div>`
    : '';

  return (
    `<div id="vd-capture-${j.id}">` +
    `<details ${open ? 'open' : ''} style="margin:12px 0;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;">` +
    '<summary style="cursor:pointer;list-style:none;padding:10px 12px;background:#f8fafc;font-size:12px;font-weight:700;color:#334155;">🗣 Voir Dire — Answer Capture (the strike basis)</summary>' +
    '<div style="padding:10px 12px;">' +
    causeBanner +
    '<p style="font-size:10px;color:var(--text-muted);margin-bottom:10px;">Tap Favorable/Unfavorable and the juror\'s demeanor — the score above updates instantly. A peremptory strike cites the recorded rating here, never a score, field, or inference.</p>' +
    rows +
    '</div></details></div>'
  );
}

/** Re-render both the capture panel and the SV assessment score for one juror. */
function refreshJurorPanels(jurorId: number, ctx: { side: Side; activeCharges: ChargeSlug[] }): void {
  const j = store.getState().jurors.find((x) => x.id === jurorId);
  if (!j) return;
  const captureEl = document.getElementById(`vd-capture-${jurorId}`);
  if (captureEl) captureEl.outerHTML = vdCaptureHtml(j, ctx, true);
  const assessEl = document.getElementById(`sv-assess-${jurorId}`);
  if (assessEl) assessEl.outerHTML = svAssessmentHtml(j, ctx, true);
}

/**
 * One-tap rating: instantly persist a favorability/demeanor/cause change and
 * refresh both panels — the real-time score update. The entire in-court
 * interaction is this one function; there is no typed input anywhere in it.
 */
export function setVdRating(
  jurorId: number,
  question: string,
  patch: Partial<Pick<VdAnswer, 'favorability' | 'demeanor' | 'cause'>>
): void {
  const st = store.getState();
  const j = st.jurors.find((x) => x.id === jurorId);
  if (!j) return;
  const list = (j.vdAnswers || []).slice();
  const rec = findOrCreate(list, question);
  const idx = list.indexOf(rec);
  list[idx] = { ...rec, ...patch, at: new Date().toISOString() };
  j.vdAnswers = list;
  store.save();
  refreshJurorPanels(jurorId, panelContext());
}
